import { useEffect } from 'react';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { withAuthenticationRequired } from '@auth0/auth0-react';

import { useAuth } from 'context/auth';
import { useEnvironment } from 'context/environment';
import { useEpisode } from 'context/episode';
import { useSelectedLocationEpisode } from 'context/locationEpisode';
import { TranslationProvider } from 'context/translations';
import { useEpisodeId } from 'hooks/useEpisodeId';
import GroupType from 'models/GroupType';
import { useTaskModalActions } from 'stores/taskModalStore';

import { ErrorState, ErrorStateKind } from '../ErrorState';
import OlioFooter from '../shared/OlioFooter';
import { UserBar } from '../UserBar';

import PatientHeader from './PatientHeader';

function PatientPage() {
  const { errorCode: emrErrorCode, decodedToken } = useAuth();
  const { environment } = useEnvironment();

  const navigate = useNavigate();
  let episodeId = useEpisodeId();
  const { episode } = useEpisode();
  const { setSelectedLocationEpisodeId, locationEpisode } = useSelectedLocationEpisode();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const deepLinkedId = searchParams.get('locationEpisodeId');

    if (deepLinkedId && episode?.locationEpisodes?.some((le) => le.id === deepLinkedId)) {
      setSelectedLocationEpisodeId(deepLinkedId);
      return;
    }

    // We do not always want to select the `latest` stay as it may be archived.
    // So instead we select the most recent stay that is not archived.
    // In the case where the ONLY stay is archived, we will select that.
    const latestUnarchivedLocationEpisode =
      episode?.locationEpisodes
        ?.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        .find((le) => !le.archived) || episode?.locationEpisodes?.[0];

    setSelectedLocationEpisodeId(latestUnarchivedLocationEpisode?.id ?? '');

    // The length of the locationEpisodes array is used to determine when this useEffect should run.
    // We only want this logic to run when a user lands on the patient page, if a new stay is created
    // from discharging, or a user clicks a link to another patient from a notification (changing the episode id).
    // If we were to update the deps to just be the episode.locationEpisodes, this logic would end up
    // running when a patient record gets canceled, and unwantingly change the locationEpisode being viewed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episode?.locationEpisodes?.length, episode?.id]);

  let emrView = false;
  if (!episodeId && decodedToken) {
    episodeId = decodedToken.episode;
    emrView = true;
  }

  const { setCurrentPatientName } = useTaskModalActions();

  useEffect(() => {
    if (environment?.OLIO_API_DOMAIN && !episodeId) {
      navigate('/patients');
    }
  }, [environment, episodeId, navigate]);

  useEffect(() => {
    setCurrentPatientName(episode?.patient?.name || '');

    return () => setCurrentPatientName('');
  }, [episode, setCurrentPatientName]);

  const error: any = emrErrorCode;

  if (error) {
    const code = typeof error === 'number' ? error : (error.response?.status ?? error.status ?? error.code);

    return (
      <ErrorState
        kind={
          code == 404
            ? ErrorStateKind.PatientNotFound
            : code == 403
              ? ErrorStateKind.RequestAccess
              : ErrorStateKind.SomethingWentWrong
        }
      />
    );
  }

  return (
    <TranslationProvider groupType={locationEpisode?.group.groupType as GroupType | undefined}>
      <Patient>
        {emrView && <UserBar />}
        <PatientHeader />
        <BodyTable>
          <ResponsiveContainer isNavBarOpen={!!episodeId}>
            <Outlet />
          </ResponsiveContainer>
        </BodyTable>
        <OlioFooter />
      </Patient>
    </TranslationProvider>
  );
}

export default PatientPage;
export const PatientPageAuth0 = withAuthenticationRequired(PatientPage, {});

const MAX_CONTENT_WIDTH = 1072;

const Patient = styled.div`
  height: 100%;
  flex-direction: column;
  overflow-x: hidden;
  @media ${({ theme }) => theme.devices.desktop} {
    display: flex;
  }
  @media not ${({ theme }) => theme.devices.desktop} {
    overflow-y: auto;
  }
`;

const BodyTable = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;

  @media (min-width: 1051px) {
    overflow-y: auto;
    padding: 0 24px;
  }
`;

export const ResponsiveContainer = styled.div<{
  isNavBarOpen?: boolean;
}>`
  max-width: ${MAX_CONTENT_WIDTH}px;
  margin-left: 0;

  @media not (max-width: ${(props) => (props.isNavBarOpen ? '1050px' : '800px')}) {
    margin-left: 32px;
  }

  @media (min-width: 1051px) {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
`;

export const ResponsiveGrid = styled.div<{
  isNavBarOpen?: boolean;
}>`
  @media (max-width: ${(props) => (props.isNavBarOpen ? '1050px' : '800px')}) {
    flex-direction: column;
  }
  display: flex;
  flex: 1;
  gap: 36px;
`;
